import { useLazyQuery } from "@apollo/client";
import { faSoapNote } from "@awesome.me/kit-0572d5a008/icons/kit/custom";
import { faFile } from "@fortawesome/free-solid-svg-icons";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  createContext,
  useContext,
  useState,
  useEffect,
} from "react";

import { useAuth } from "./use-auth";
import { useLocalStorage } from "./use-local-storage";
import { useTeam } from "./use-team";
import { SCRIBENOTE_DEFAULTS } from "../components/common/record/constants";
import { GET_AUTO_TEMPLATES } from "../graphql/queries/AutoTemplates";

const RecordingJobTypeContext = createContext();

const DEFAULT_JOB_TYPE = {
  value: "medical_record", // The will be a UUID for templates
  name: "Medical Record",
  icon: faSoapNote,
  description:
    "Select 'Medical Record' to generate an automatic SOAP note for your patient.",
};

// For defaults, compare storedValue to option.value;
// For custom templates, compare storedValue to template.uuid.
function getMatchingJobType(storedValue, autoTemplates) {
  let match = SCRIBENOTE_DEFAULTS.find(
    (option) => option.value === storedValue,
  );
  if (!match && autoTemplates && Array.isArray(autoTemplates)) {
    const templateMatch = autoTemplates.find(
      (template) => template.uuid === storedValue,
    );
    if (templateMatch) {
      match = {
        value: templateMatch.uuid,
        name: templateMatch.name,
        description: templateMatch.description,
        templateType: templateMatch.templateType,
        icon: templateMatch.icon || faFile,
      };
    }
  }
  return match;
}

export const RecordingJobTypeProvider = ({ children }) => {
  const [selectedJobType, setSelectedJobType] = useState(null);
  const [filterJobType, setFilterJobType] = useLocalStorage(
    "filter-job-type",
    null,
  );
  const [templates, setTemplates] = useState([]);
  const { enableAdaptiveTemplates } = useFlags();
  const { currentTeam } = useTeam();
  const auth = useAuth();

  const resetSelectedJobType = () =>
    setSelectedJobType(DEFAULT_JOB_TYPE);

  useEffect(() => {
    if (auth?.isUserAuthenticated) {
      getAutoTemplates();
    } else {
      setFilterJobType(null);
    }
  }, [auth?.isUserAuthenticated]);

  const [getAutoTemplates, { data, refetch: refetchTemplates }] =
    useLazyQuery(GET_AUTO_TEMPLATES, {
      variables: {
        teamUuid: currentTeam?.uuid,
      },
      skip: !enableAdaptiveTemplates,
    });

  useEffect(() => {
    if (enableAdaptiveTemplates && data?.autoTemplates) {
      setTemplates(data.autoTemplates || []);

      const storedValue = localStorage.getItem("recordingJobType");
      if (storedValue) {
        const match = getMatchingJobType(
          storedValue,
          data?.autoTemplates,
        );
        setSelectedJobType(match || DEFAULT_JOB_TYPE);
      } else {
        setSelectedJobType(DEFAULT_JOB_TYPE);
      }
    }
  }, [data, enableAdaptiveTemplates]);

  useEffect(() => {
    if (selectedJobType) {
      localStorage.setItem("recordingJobType", selectedJobType.value);
    }
  }, [selectedJobType]);

  return (
    <RecordingJobTypeContext.Provider
      value={{
        selectedJobType,
        setSelectedJobType,
        resetSelectedJobType,
        filterJobType,
        setFilterJobType,
        templates,
        refetchTemplates,
      }}
    >
      {children}
    </RecordingJobTypeContext.Provider>
  );
};

export const useRecordingJobType = () => {
  const context = useContext(RecordingJobTypeContext);
  if (!context) {
    throw new Error(
      "useRecordingJobType must be used within a RecordingJobTypeProvider",
    );
  }
  return context;
};
